import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import { useParams, useNavigate } from "react-router-dom";
import { GlobalContext } from "../../GlobalProvider";
import Header from "../Header";
import {
  getAIAgentById,
  updateAIAgent,
  createAIAgent,
  getAIDocuments,
  uploadAIDocument,
  getAIAgentTemplates,
} from "../../api";

const CreateEditAIAgent = () => {
  const { id } = useParams();
  const { fullApp } = useContext(GlobalContext);
  const navigate = useNavigate();
  const [isEditMode, setIsEditMode] = useState(false);
  const [agentDetails, setAgentDetails] = useState({
    agentName: "",
    appId: "",
    accessRole: "access_app",
    description: "",
    queryTemplate: "",
    systemPrompt: "",
    emptyPrompt: "",
    dataSourceType: "",
    dataSourceConfig: [],
    implementedBy: "",
    capabilities: [{ name: "", description: "" }],
    isActionTemplate: false,
  });
  const [availableFiles, setAvailableFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const [newFile, setNewFile] = useState(null);
  const [templates, setTemplates] = useState([]); // Templates list
  const [selectedTemplate, setSelectedTemplate] = useState(""); // Selected template


  useEffect(() => {
    if (id) {
      setIsEditMode(true);
      fetchAgentDetails(id);
    } else {
      fetchTemplates();
    }
    fetchFiles();
  }, [id]);

  const fetchAgentDetails = async (agentId) => {
    try {
      const response = await getAIAgentById(agentId);
      let agentData = response.data.data;
      setAgentDetails(agentData);
      setSelectedFiles(agentData.dataSourceConfig);
    
    } catch (err) {
      console.error("Error fetching agent details:", err);
    }
  };

  const fetchFiles = async () => {
    try {
      const response = await getAIDocuments();
      setAvailableFiles(response.data);
    } catch (err) {
      console.error("Error fetching documents:", err);
    }
  };
  const fetchTemplates = async () => {
    try {
      const response = await getAIAgentTemplates();
      setTemplates(response.data.data); // Update with fetched templates
    } catch (err) {
      console.error("Error fetching templates:", err);
    }
  };

  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);
    const template = templates.find((t) => t._id === templateId);
    if (template) {
      setAgentDetails((prev) => ({
        ...prev,
        ...{
          agentName: template.agentName, systemPrompt: template.systemPrompt,
          capabilities: template.capabilities,
          queryTemplate: template.queryTemplate, implementedBy: template.implementedBy, emptyPrompt: template.emptyPrompt, description: template.description, dataSourceType: template.dataSourceType
        }, // Spread template details
        dataSourceConfig: template.dataSourceConfig || [],
      }));
    }
  };

  const handleAddFile = (file) => {
    setSelectedFiles((prev) => [...prev, file]);
    setAvailableFiles((prev) => prev.filter((f) => f._id !== file._id));
  };

  const handleRemoveFile = (fileId) => {
    const removedFile = selectedFiles.find((file) => file._id === fileId);
    setSelectedFiles((prev) => prev.filter((file) => file._id !== fileId));
    if (removedFile) {
      setAvailableFiles((prev) => [...prev, removedFile]);
    }
  };

  const handleFileUpload = async () => {
    if (newFile) {
      try {
        const formData = new FormData();
        formData.append("file", newFile);
        const response = await uploadAIDocument(formData);
        handleAddFile(response.data.data);
        alert("File uploaded successfully!");
        setNewFile(null);
      } catch (err) {
        console.error("Error uploading file:", err);
      }
    }
  };

  const handleInputChange = (field, value) => {
    setAgentDetails((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...agentDetails,
        dataSourceConfig: selectedFiles,
      };
      if (isEditMode) {
        await updateAIAgent(id, payload);
        alert("AI Agent updated successfully!");
      } else {
        payload.appId = fullApp._id;
        await createAIAgent(payload);
        alert("AI Agent created successfully!");
      }
      navigate("/agents");
    } catch (err) {
      console.error("Error saving AI Agent:", err);
    }
  };

  return (
    <Box p={3}>
      <Header title=  {isEditMode ? "Edit AI Agent" : "Create AI Agent"} />
     
      <Box display="flex" flexDirection="column" gap={3}>
        {!isEditMode && (
          <FormControl fullWidth>
            <InputLabel>Select a Template</InputLabel>
            <Select
              value={selectedTemplate}
              onChange={(e) => handleTemplateChange(e.target.value)}
            >
              {templates.map((template) => (
                <MenuItem key={template._id} value={template._id}>
                  {template.agentName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <TextField
          label="Agent Name"
          fullWidth
          value={agentDetails.agentName}
          onChange={(e) => handleInputChange("agentName", e.target.value)}
        />
        <TextField
          label="Description"
          fullWidth
          multiline
          rows={3}
          value={agentDetails.description}
          onChange={(e) => handleInputChange("description", e.target.value)}
        />
        <TextField
          label="Query Template"
          fullWidth
          multiline
          rows={3}
          value={agentDetails.queryTemplate}
          onChange={(e) => handleInputChange("queryTemplate", e.target.value)}
        />
        <TextField
          label="System Prompt"
          fullWidth
          multiline
          rows={3}
          value={agentDetails.systemPrompt}
          onChange={(e) => handleInputChange("systemPrompt", e.target.value)}
        />
        <TextField
          label="Empty Prompt"
          fullWidth
          multiline
          rows={3}
          value={agentDetails.emptyPrompt}
          onChange={(e) => handleInputChange("emptyPrompt", e.target.value)}
        />
        <FormControl fullWidth>
          <InputLabel>Data Source Type</InputLabel>
          <Select
            value={agentDetails.dataSourceType}
            onChange={(e) =>
              handleInputChange("dataSourceType", e.target.value)
            }
          >
            <MenuItem value="files">Files</MenuItem>
            <MenuItem value="database">Database</MenuItem>
            <MenuItem value="functioncall">Function Call</MenuItem>
          </Select>
        </FormControl>
        {agentDetails.dataSourceType === "files" && (
          <Box>
            <Typography variant="h6">Data Source Files</Typography>
            <List>
              {selectedFiles.map((file) => (
                <ListItem
                  key={file._id}
                  secondaryAction={
                    <IconButton edge="end" onClick={() => handleRemoveFile(file._id)}>
                      <Delete />
                    </IconButton>
                  }
                >
                  <ListItemText primary={file.file} />
                </ListItem>
              ))}
            </List>
            <Typography variant="body1" mt={2}>
              Available Files:
            </Typography>
            <List>
              {availableFiles.map((file) => (
                <ListItem
                  key={file._id}
                  secondaryAction={
                    <IconButton edge="end" onClick={() => handleAddFile(file)}>
                      <AddIcon />
                    </IconButton>
                  }
                >
                  <ListItemText primary={file.file} />
                </ListItem>
              ))}
            </List>
            <Box display="flex" alignItems="center" gap={2} mt={2}>
              <TextField
                type="file"
                onChange={(e) => setNewFile(e.target.files[0])}
                fullWidth
              />
              <Button variant="contained" color="primary" onClick={handleFileUpload}>
                Upload
              </Button>
            </Box>
          </Box>
        )}
        {/* <Box>
          <Typography variant="h6">Capabilities</Typography>
          {agentDetails.capabilities.map((capability, index) => (
            <Box key={index} display="flex" flexDirection="column" gap={2} mt={2}>
              <TextField
                label="Capability Name"
                fullWidth
                value={capability.name}
                onChange={(e) =>
                  setAgentDetails((prev) => {
                    const updatedCapabilities = [...prev.capabilities];
                    updatedCapabilities[index].name = e.target.value;
                    return { ...prev, capabilities: updatedCapabilities };
                  })
                }
              />
              <TextField
                label="Capability Description"
                fullWidth
                multiline
                rows={2}
                value={capability.description}
                onChange={(e) =>
                  setAgentDetails((prev) => {
                    const updatedCapabilities = [...prev.capabilities];
                    updatedCapabilities[index].description = e.target.value;
                    return { ...prev, capabilities: updatedCapabilities };
                  })
                }
              />
            </Box>
          ))}
          <Button
            variant="outlined"
            onClick={() =>
              setAgentDetails((prev) => ({
                ...prev,
                capabilities: [...prev.capabilities, { name: "", description: "" }],
              }))
            }
            sx={{ mt: 2 }}
          >
            Add Capability
          </Button>
        </Box> */}
        <FormControlLabel
          control={
            <Checkbox
              checked={agentDetails.isActionTemplate}
              onChange={(e) => handleInputChange("isActionTemplate", e.target.checked)}
            />
          }
          label="Is Action Template"
        />
        <Button variant="contained" color="primary" onClick={handleSubmit}>
          {isEditMode ? "Update" : "Create"}
        </Button>
      </Box>
    </Box>
  );
};

export default CreateEditAIAgent;
